import { useCallback, useRef } from 'react';
import posthog from 'posthog-js';
import { DeviceType } from '@/lib/constants/foxway';

import { useStore } from '../hooks/useStore';
import useTranslation from '../hooks/useTranslation';

import Laptop from '../components/icons/Laptop';
import Smartwatch from '../components/icons/Smartwatch';
import Tablet from '../components/icons/Tablet';
import Mobile from '../components/icons/Mobile';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/lib/components/ui/tabs';
import { Button } from '@/lib/components/ui/button';
import ModelSelect from './ModelSelect';

type DeviceIcon = {
  icon: DeviceType;
  active?: boolean;
};

const DeviceIcon = ({ icon, active }: DeviceIcon) => {
  switch (icon) {
    case 'mobile':
      return <Mobile active={active} className={`size-7`} />;
    case 'tablet':
      return <Tablet active={active} className={`size-7`} />;
    case 'watch':
      return <Smartwatch active={active} className={`size-7`} />;
    case 'computer':
      return <Laptop active={active} className={`size-7`} />;
    default:
      return null;
  }
};

const DeviceTypeSelect = () => {
  const selectedDeviceType = useStore((state) => state.selectedDeviceType);
  const setSelectedDeviceType = useStore(
    (state) => state.setSelectedDeviceType,
  );
  const translations = useTranslation();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleClick = useCallback(
    (deviceType: DeviceType) => () => {
      setSelectedDeviceType(deviceType);
      posthog.capture('Select Device Type', { deviceType });
    },
    [setSelectedDeviceType],
  );

  const deviceTypes = Object.keys(DeviceType) as DeviceType[];

  return (
    <Tabs
      defaultValue={deviceTypes[0]}
      className={'gap-0'}
      orientation="horizontal"
    >
      <TabsList
        className="grid w-full h-auto grid-cols-4 bg-tabsBackground border-none p-0"
        aria-orientation="horizontal"
        onKeyDown={(e) => {
          if (!selectedDeviceType) return;
          const currentIndex = deviceTypes.indexOf(selectedDeviceType);
          if (e.key === 'ArrowRight') {
            const nextIndex = (currentIndex + 1) % deviceTypes.length;
            setSelectedDeviceType(deviceTypes[nextIndex]);
            tabRefs.current[nextIndex]?.focus();
          } else if (e.key === 'ArrowLeft') {
            const prevIndex =
              (currentIndex - 1 + deviceTypes.length) % deviceTypes.length;
            tabRefs.current[prevIndex]?.focus();
            setSelectedDeviceType(deviceTypes[prevIndex]);
          }
        }}
      >
        {deviceTypes.map((deviceType, index) => (
          <Button
            key={deviceType}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            variant="ghost"
            role="tab"
            tabIndex={deviceType === selectedDeviceType ? 0 : -1}
            aria-selected={deviceType === selectedDeviceType}
            aria-label={translations[deviceType]}
            onClick={handleClick(deviceType)}
            className={`p-3 sm:py-4 sm:px-6 rounded-t-lg rounded-b-none flex flex-col h-auto text-base text-black ${
              deviceType === selectedDeviceType
                ? 'bg-white border-samsungBlue'
                : 'border-neutral-300'
            } border-t-0 border-x-0 border-b-4`}
          >
            <DeviceIcon icon={deviceType} />
            {translations[deviceType]}
          </Button>
        ))}
      </TabsList>
      <div className={'bg-white px-4 py-4 sm:px-10 sm:py-7 rounded-b-lg'}>
        <ModelSelect />
      </div>
    </Tabs>
  );
};

export default DeviceTypeSelect;
